import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Bot, Gift, CheckCircle, MessageSquare } from 'lucide-react';

const API = '/api';

export default function Register({ setAuth, onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [referralCode, setReferralCode] = useState('');
  const [referralValid, setReferralValid] = useState(null);
  const [referralMessage, setReferralMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Check for referral code in URL
    const ref = searchParams.get('ref');
    if (ref) {
      setReferralCode(ref.toUpperCase());
      validateReferralCode(ref);
    }
  }, [searchParams]);

  const validateReferralCode = async (code) => {
    try {
      const response = await axios.get(`${API}/auth/validate-referral/${code}`);
      setReferralValid(response.data.valid);
      setReferralMessage(response.data.message);
    } catch (error) {
      setReferralValid(false);
      setReferralMessage('Erro ao validar código');
    }
  };

  const handleReferralCodeChange = (e) => {
    const code = e.target.value.toUpperCase();
    setReferralCode(code);
    if (code.length >= 8) {
      validateReferralCode(code);
    } else {
      setReferralValid(null);
      setReferralMessage('');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    setLoading(true);

    try {
      const payload = { email, password };
      if (referralCode && referralValid) {
        payload.referral_code = referralCode;
      }
      
      const response = await axios.post(`${API}/auth/register`, payload);
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setAuth(true);
      
      // Check subscription status after register
      if (onLogin) {
        await onLogin();
      }
      
      const successMsg = referralCode && referralValid 
        ? 'Conta criada com indicação! 7 dias grátis ativados!'
        : 'Conta criada com sucesso! 7 dias grátis ativados!';
      toast.success(successMsg);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar conta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-24 h-24 mx-auto bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl shadow-md shadow-emerald-500/30 mb-4 flex items-center justify-center">
            <MessageSquare className="w-12 h-12 text-white" strokeWidth={2.5} />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">MeuGestor</h1>
          <p className="text-slate-400">Crie sua conta e teste 7 dias grátis</p>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 shadow-xl">
          <form onSubmit={handleRegister} className="space-y-6" data-testid="register-form">
            <div>
              <Label htmlFor="email" className="text-white">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2"
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-white">Senha</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="mt-2"
                data-testid="password-input"
              />
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-white">Confirmar Senha</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                className="mt-2"
                data-testid="confirm-password-input"
              />
            </div>

            {/* Código de Indicação */}
            <div>
              <Label htmlFor="referralCode" className="text-white flex items-center gap-2">
                <Gift className="w-4 h-4 text-emerald-500" />
                Código de Indicação (opcional)
              </Label>
              <Input
                id="referralCode"
                type="text"
                placeholder="Ex: ABC12345"
                value={referralCode}
                onChange={handleReferralCodeChange}
                maxLength={8}
                className="mt-2 uppercase"
                data-testid="referral-code-input"
              />
              {referralMessage && (
                <p className={`text-xs mt-1 flex items-center gap-1 ${
                  referralValid ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  {referralValid && <CheckCircle className="w-3 h-3" />}
                  {referralMessage}
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full bg-violet-600 hover:bg-violet-700 text-white font-medium shadow-lg shadow-violet-500/20 transition-all hover:-translate-y-0.5 h-12 rounded-md"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? 'Criando conta...' : 'Criar Conta e Testar Grátis'}
            </Button>
          </form>

          <div className="mt-6 text-center text-slate-400">
            <p>
              Já tem uma conta?{' '}
              <Link to="/login" className="text-violet-600 hover:text-violet-500 font-medium">
                Faça login
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
